import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useCartStore } from '@/store/cartStore'
import { useOrderStore } from '@/store/orderStore'
import { getShippingCost } from '@/services/productCatalogService'
import { processPayment, PaymentMethod, CheckoutData } from '@/services/payment/paymentService'
import { Address } from '@/types/order'
import { AddressFormData } from '@/components/shop/AddressForm'
import AddressForm from '@/components/shop/AddressForm'
import PaymentForm from '@/components/shop/PaymentForm'
import toast from 'react-hot-toast'
import { FaShoppingCart, FaMapMarkerAlt, FaCreditCard, FaLock, FaPaypal, FaTrash, FaTruck, FaShieldAlt, FaCheck } from 'react-icons/fa'
import { FcGoogle } from 'react-icons/fc'
import RealisticPlate from '@/components/shop/RealisticPlate'
import { PlateSize, PlateType, VehicleCategory } from '@/services/rules/plateRulesEngine'
import { formatCurrency } from '@/utils/helpers'

const Checkout = () => {
  const navigate = useNavigate()
  const { items, getTotalPrice, clearCart, removeItem } = useCartStore()
  const { addOrder } = useOrderStore()

  const [email, setEmail] = useState('')
  const [deliveryAddress, setDeliveryAddress] = useState<Address | null>(null)
  const [invoiceAddress, setInvoiceAddress] = useState<Address | null>(null)
  const [sameAsDelivery, setSameAsDelivery] = useState(true)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)

  const subtotal = getTotalPrice()
  const totalItems = items.reduce((s, i) => s + i.quantity, 0)
  const [shippingCost, setShippingCost] = useState(3.45)
  useEffect(() => { getShippingCost().then(setShippingCost) }, [])
  const hasPhysicalItems = items.length > 0
  const shipping = hasPhysicalItems ? shippingCost : 0
  const totalPrice = subtotal + shipping
  const vatAmount = totalPrice * 19 / 119

  const plateItem = items.find(item => item.productId.startsWith('plate-') || item.productId.startsWith('PLATE-'))

  const handleDeliveryAddressSubmit = (data: AddressFormData) => {
    setDeliveryAddress(data)
    if (sameAsDelivery) setInvoiceAddress(data)
  }

  const handleInvoiceAddressSubmit = (data: AddressFormData) => {
    setInvoiceAddress(data)
  }

  const handleSameAsDeliveryChange = (checked: boolean) => {
    setSameAsDelivery(checked)
    if (checked && deliveryAddress) setInvoiceAddress(deliveryAddress)
  }

  const handlePlaceOrder = async () => {
    if (!email) { toast.error('Bitte E-Mail-Adresse eingeben'); return }
    if (!deliveryAddress) { toast.error('Bitte Lieferadresse eingeben'); return }
    if (!invoiceAddress) { toast.error('Bitte Rechnungsadresse eingeben'); return }
    if (!selectedPaymentMethod) { toast.error('Bitte Zahlungsmethode wählen'); return }
    if (items.length === 0) { navigate('/shop'); return }

    setIsProcessing(true)
    try {
      const checkoutData: CheckoutData = {
        email,
        deliveryAddress,
        invoiceAddress,
        paymentMethod: selectedPaymentMethod,
        items,
        totalPrice,
      }
      const result = await processPayment(checkoutData)
      if (result.success && result.order) {
        addOrder(result.order)
        clearCart()
        toast.success('Bestellung erfolgreich aufgegeben!')
        navigate(`/orders?orderId=${result.order.id}`)
      } else {
        toast.error(result.error || 'Fehler beim Aufgeben der Bestellung')
      }
    } catch (error: any) {
      toast.error(error.message || 'Fehler beim Aufgeben der Bestellung')
    } finally {
      setIsProcessing(false)
    }
  }

  // Empty cart
  if (items.length === 0) {
    return (
      <div className="bg-dark-950 min-h-screen flex items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="w-20 h-20 rounded-full bg-dark-800 flex items-center justify-center mx-auto mb-5">
            <FaShoppingCart className="w-8 h-8 text-gray-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-300 mb-2">Ihr Warenkorb ist leer</h2>
          <p className="text-gray-500 text-sm mb-6">Fügen Sie Produkte hinzu, um fortzufahren</p>
          <button
            onClick={() => navigate('/shop')}
            className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-semibold transition-colors"
          >
            Zum Shop
          </button>
        </motion.div>
      </div>
    )
  }

  const canOrder = !!deliveryAddress && !!invoiceAddress && !!selectedPaymentMethod && !!email

  return (
    <div className="bg-dark-950 min-h-screen py-8 sm:py-12">
      <div className="container-custom px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Kasse</h1>
          <p className="text-gray-500 text-sm mt-1">{totalItems} Artikel im Warenkorb</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">

          {/* ── Left: Forms (3 cols) ────────────────────────────── */}
          <div className="lg:col-span-3 space-y-5">

            {/* Email */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="bg-dark-900/60 border border-dark-700/40 rounded-xl p-5"
            >
              <label className="block text-sm font-semibold text-gray-300 mb-2">E-Mail-Adresse *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                placeholder="max.mustermann@example.com"
              />
            </motion.div>

            {/* Delivery Address */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-dark-900/60 border border-dark-700/40 rounded-xl p-5"
            >
              <div className="flex items-center gap-2 mb-4">
                <FaMapMarkerAlt className="w-4 h-4 text-primary-400" />
                <h2 className="text-base font-bold text-white">Lieferadresse</h2>
              </div>
              <AddressForm onSubmit={handleDeliveryAddressSubmit} defaultValues={deliveryAddress || undefined} />
            </motion.div>

            {/* Invoice Address */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="bg-dark-900/60 border border-dark-700/40 rounded-xl p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="w-4 h-4 text-primary-400" />
                  <h2 className="text-base font-bold text-white">Rechnungsadresse</h2>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sameAsDelivery}
                    onChange={(e) => handleSameAsDeliveryChange(e.target.checked)}
                    className="w-4 h-4 text-primary-500 bg-dark-800 border-dark-700 rounded focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-400">Gleich wie Lieferadresse</span>
                </label>
              </div>
              {sameAsDelivery ? (
                <p className="text-gray-500 text-sm">Rechnungsadresse entspricht der Lieferadresse</p>
              ) : (
                <AddressForm onSubmit={handleInvoiceAddressSubmit} defaultValues={invoiceAddress || undefined} />
              )}
            </motion.div>

            {/* Payment */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="bg-dark-900/60 border border-dark-700/40 rounded-xl p-5"
            >
              <div className="flex items-center gap-2 mb-4">
                <FaCreditCard className="w-4 h-4 text-primary-400" />
                <h2 className="text-base font-bold text-white">Zahlungsmethode</h2>
              </div>
              <PaymentForm selectedMethod={selectedPaymentMethod} onMethodSelect={setSelectedPaymentMethod} />
            </motion.div>
          </div>

          {/* ── Right: Order Summary (2 cols) ──────────────────── */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-dark-900/80 border border-dark-700/40 rounded-xl overflow-hidden sticky top-6"
            >
              {/* Summary header */}
              <div className="px-5 py-4 border-b border-dark-700/40">
                <h2 className="text-base font-bold text-white">Bestellübersicht</h2>
              </div>

              {/* Plate preview */}
              {plateItem?.customization && (
                <div className="px-5 pt-4 flex justify-center">
                  <div style={{ transform: 'scale(0.48)', transformOrigin: 'top center', height: 60 }}>
                    <RealisticPlate
                      plateSize={plateItem.customization.plateSize as PlateSize || { width: 520, height: 110, layout: 'einzeilig' }}
                      plateType={plateItem.customization.plateType as PlateType || 'REGULAR'}
                      vehicleCategory={plateItem.customization.vehicleCategory as VehicleCategory || 'PKW'}
                      input={{
                        cityCode: String(plateItem.customization.cityCode || ''),
                        letters: String(plateItem.customization.letters || ''),
                        numbers: String(plateItem.customization.numbers || ''),
                      }}
                      interactive={false}
                    />
                  </div>
                </div>
              )}

              {/* Items list */}
              <div className="px-5 py-4 space-y-3">
                {items.map((item) => (
                  <div key={item.productId} className="flex items-start justify-between gap-2 group">
                    <div className="flex items-start gap-2 min-w-0">
                      <FaCheck className="w-3 h-3 text-primary-500 mt-1 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-200 leading-snug truncate">{item.productName}</p>
                        <p className="text-xs text-gray-500">{item.quantity} Stück × {formatCurrency(item.price)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-sm font-semibold text-white">{formatCurrency(item.price * item.quantity)}</span>
                      <button
                        onClick={() => removeItem(item.productId)}
                        className="text-gray-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="px-5 py-4 border-t border-dark-700/40 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Zwischensumme ({totalItems} Artikel)</span>
                  <span className="text-gray-200">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Versand</span>
                  <span className={shipping === 0 ? "text-green-400 font-medium" : "text-gray-200"}>
                    {shipping === 0 ? 'Kostenlos' : formatCurrency(shipping)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">inkl. MwSt. (19%)</span>
                  <span className="text-gray-500">{formatCurrency(vatAmount)}</span>
                </div>
                <div className="border-t border-dark-600/50 pt-3 flex justify-between items-end">
                  <span className="text-gray-300 font-semibold">Gesamt</span>
                  <span className="text-2xl font-black text-white">{formatCurrency(totalPrice)}</span>
                </div>
              </div>

              {/* Quick pay buttons */}
              <div className="px-5 pb-4 space-y-2">
                <button className="w-full flex items-center justify-center gap-2 bg-[#ffc439] hover:bg-[#f4b828] text-[#111] font-bold py-2.5 rounded-lg transition-colors text-sm">
                  <FaPaypal className="w-4 h-4 text-[#003087]" />
                  PayPal
                </button>
                <button className="w-full flex items-center justify-center gap-2 bg-black hover:bg-gray-900 border border-gray-700 text-white font-bold py-2.5 rounded-lg transition-colors text-sm">
                  <FcGoogle className="w-4 h-4" />
                  Google Pay
                </button>

                <div className="flex items-center py-2">
                  <div className="h-px bg-dark-700 flex-1" />
                  <span className="px-3 text-[10px] text-gray-600 uppercase tracking-wider">oder</span>
                  <div className="h-px bg-dark-700 flex-1" />
                </div>

                <button
                  onClick={handlePlaceOrder}
                  disabled={isProcessing || !canOrder}
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg font-bold text-sm transition-all ${
                    isProcessing || !canOrder
                      ? 'bg-dark-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 text-white shadow-lg shadow-primary-500/20'
                  }`}
                >
                  <FaLock className="w-3.5 h-3.5" />
                  {isProcessing ? 'Wird verarbeitet...' : 'Jetzt bestellen'}
                </button>
              </div>

              {/* Trust */}
              <div className="px-5 py-3 border-t border-dark-700/30 flex items-center justify-center gap-5">
                <div className="flex items-center gap-1.5 text-gray-500 text-[10px]">
                  <FaShieldAlt className="w-3 h-3" />
                  <span>SSL-verschlüsselt</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-500 text-[10px]">
                  <FaTruck className="w-3 h-3" />
                  <span>Schnelle Lieferung</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Checkout
